/* PavCon, LLC Proprietary.All rights reserved 2022 */

import { ErrorHandler, NgModule, APP_INITIALIZER } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { provideNgxMask } from 'ngx-mask';
import { MetaReducer, StoreModule } from '@ngrx/store';
import { NgxUiLoaderConfig, NgxUiLoaderModule } from 'ngx-ui-loader';

import { AppComponent } from './app.component';
import { AppRoles } from './app-roles';
import { AppRoutingModule } from './app-routing.module';
import { AuthGuard } from './shared/guards/auth.guard';
import { AuthLazyGuard } from './shared/guards/auth-lazy.guard';
import { BasesService } from './shared/services/bases.service';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import CognitoService from './shared/services/cognito';
import { CommonModule } from '@angular/common';
import { CustomEmailValidator } from './authentication/sign-up/helpers/must-match.validator';
import { EffectsModule } from '@ngrx/effects';
import { GlobalErrorHandler } from './shared/handlers/global-error.handler';
import { HeaderInterceptor } from './shared/interceptors/header-inteceptors';
import { IState } from '../app/shared/store/root.store';
// import { LocalStorageModule } from 'angular-2-local-storage'; // Replaced with ngx-webstorage-service
import { MainSharedModule } from './shared/modules/mainShared.module';
import { MajcomService } from './shared/services/majcom.service';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { PwcsService } from './shared/services/pwcs.service';
// import { ResourceModule } from '@ngx-resource/handler-ngx-http';
import { ResponseInterceptor } from './shared/interceptors/response-interceptor';
import { SettingsService } from './shared/services/settings.service';
import { ShopService } from './shared/services/shop.service';
import { SignUpFormValidationService } from './shared/services/signUpFormValidationService.service';
import { SignUpService } from './shared/services/signUp.service';
import { StorageServiceModule } from 'ngx-webstorage-service';
import { SubscriptionManager } from './shared/managers/subscription.manager';
import { UnitService } from './shared/services/units.service';
import { appReducer } from './shared/store/root.store';
import { effects } from './shared/store/effects/index';
import { environment } from '../environments/environment'; // Angular CLI environment
// import { localStorageServiceConfig } from './app.local-storage'; // No longer needed with ngx-webstorage-service
import { storeFreeze } from 'ngrx-store-freeze';
import { BnNgIdleService } from 'bn-ng-idle';
import { AppInitializationService } from './shared/services/app-initialization.service';

export const metaReducers: MetaReducer<IState>[] = !environment.production ? [storeFreeze] : [];

// App initialization factory
export function initializeAppFactory(appInitService: AppInitializationService): () => Promise<void> {
  return () => appInitService.initializeApp();
}

const ngxUiLoaderConfig: NgxUiLoaderConfig = {
  bgsColor: '#0B1C41',
  fgsPosition: 'bottom-right',
  fgsSize: 60,
  fgsType: 'three-strings',
};

// export function ResourceHandlerFactory(http: HttpClient) {
//   return ResourceHandlerFactory(http);
// }

@NgModule({
  declarations: [AppComponent],
  imports: [
    CommonModule,
    MatSnackBarModule,
    BrowserModule,
    BrowserAnimationsModule,
    MainSharedModule,
    // Mask library is provided via providers using provideNgxMask()
    StorageServiceModule,
    AppRoutingModule,
    NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
    // LocalStorageModule.forRoot(localStorageServiceConfig), // Replaced with ngx-webstorage-service
    EffectsModule.forRoot(effects),
    StoreModule.forRoot(appReducer, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
      },
    }),
    HttpClientModule,
    // ResourceModule.forRoot(),
    NgbModule,
    MatDialogModule,
  ],
  exports: [MatDialogModule],
  providers: [
    provideNgxMask(),
    { provide: HTTP_INTERCEPTORS, useClass: HeaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ResponseInterceptor, multi: true },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      deps: [AppInitializationService],
      multi: true
    },
    BnNgIdleService,
    BasesService,
    PwcsService,
    SignUpService,
    CustomEmailValidator,
    SignUpFormValidationService,
    MajcomService,
    ShopService,
    CognitoService,
    AuthGuard,
    AuthLazyGuard,
    MatSnackBarModule,
    AppRoles,
    UnitService,
    SettingsService,
    SubscriptionManager,
    AppInitializationService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }

/* PavCon, LLC Proprietary.All rights reserved 2022 */
